import React, { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { DataLabel } from "../components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Sparkle, PaperPlaneTilt, Trash, UploadSimple, Eye, CircleNotch, Check, X, ArrowsClockwise } from "@phosphor-icons/react";

const LANGS = [["es", "Español"], ["fr", "Français"], ["en", "English"]];
const ALL_LANGS = [["es", "Español"], ["fr", "Français"], ["en", "English"], ["ru", "Русский"]];
const CONSENT = { active: ["Actif", "#166534"], pending: ["En attente", "#b45309"], unsubscribed: ["Désinscrit", "#dc2626"], invalid: ["Invalide", "#94a3b8"] };
const PROP_STATUS = { proposed: ["Proposé", "#b45309"], approved: ["Programmé", "#2563EB"], sent: ["Envoyé", "#166534"], rejected: ["Refusé", "#94a3b8"] };

function Pill({ map, k }) { const [l, c] = map[k] || [k, "#64748b"]; return <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full" style={{ background: c + "1a", color: c }}>{l}</span>; }

function relDays(iso) {
  if (!iso) return "";
  const then = new Date(iso), now = new Date();
  const days = Math.floor((now - then) / 86400000);
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return "hier";
  if (days < 7) return `il y a ${days} jours`;
  if (days < 30) { const w = Math.floor(days / 7); return `il y a ${w} semaine${w > 1 ? "s" : ""}`; }
  const m = Math.floor(days / 30); return `il y a ${m} mois`;
}

async function postSend(id, body) {
  try { return (await api.post(`/newsletter/proposals/${id}/send`, body)).data; }
  catch (e) {
    const d = e?.response?.data?.detail;
    if (d?.code !== "recent_campaign") throw e;
    if (!window.confirm(`${d.message}\n\nEnvoyer quand même ?`)) return null;
    return (await api.post(`/newsletter/proposals/${id}/send`, { ...body, confirm_recent: true })).data;
  }
}

export default function Newsletter() {
  const [tab, setTab] = useState("proposals");
  const [proposals, setProposals] = useState([]);
  const [contacts, setContacts] = useState({ contacts: [], counts: {} });
  const [suppression, setSuppression] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", lang: "es", consent_status: "active" });
  const [sync, setSync] = useState(null);
  const [selected, setSelected] = useState([]);
  const [source, setSource] = useState("auto");
  const [articleUrl, setArticleUrl] = useState("");
  const [confirmReal, setConfirmReal] = useState(false);
  const [contactFilter, setContactFilter] = useState("all");
  const [openBroadcast, setOpenBroadcast] = useState(null);
  const [mail, setMail] = useState({ brief: "", tone: "chaleureux", languages: ["es", "fr", "en", "ru"], signature: "Dr Florian A. Vallecillo Cabrera · Clínica Valorian" });
  const [templates, setTemplates] = useState([]);
  const [langBreak, setLangBreak] = useState(null);
  const [history, setHistory] = useState({ campaigns: [], last_sent_at: null, total: 0 });
  const [deliveryId, setDeliveryId] = useState(null);
  const loadHistory = () => api.get("/newsletter/history").then((r) => setHistory(r.data)).catch(() => {});
  const loadTemplates = () => api.get("/newsletter/templates").then((r) => setTemplates(r.data)).catch(() => {});
  const loadLangBreak = () => api.get("/newsletter/contacts/lang-breakdown").then((r) => setLangBreak(r.data)).catch(() => {});
  const cancelSchedule = async (pid, e) => {
    e && e.stopPropagation();
    if (!window.confirm("Annuler la programmation de cet envoi ?")) return;
    try { await api.post(`/newsletter/proposals/${pid}/cancel-schedule`); toast.success("Programmation annulée"); loadProposals(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Annulation impossible"); }
  };
  const applyTemplate = async (tid) => {
    try { const { data } = await api.post(`/newsletter/templates/${tid}/use`); toast.success("Modèle chargé"); await loadProposals(); setOpenBroadcast(data.id); }
    catch { toast.error("Impossible d'utiliser ce modèle"); }
  };
  const delTemplate = async (tid, e) => {
    e && e.stopPropagation();
    if (!window.confirm("Supprimer ce modèle ?")) return;
    try { await api.delete(`/newsletter/templates/${tid}`); toast.success("Modèle supprimé"); loadTemplates(); } catch { toast.error("Échec"); }
  };
  const toggleLang = (code) => setMail((m) => ({ ...m, languages: m.languages.includes(code) ? m.languages.filter((x) => x !== code) : [...m.languages, code] }));
  const generateCampaign = async () => {
    if (!mail.brief.trim()) { toast.error("Écrivez d'abord le brief du message"); return; }
    if (!mail.languages.length) { toast.error("Choisissez au moins une langue"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/campaigns/generate", mail);
      toast.success("Message généré par l'IA", { description: `Langues : ${(data.langs || []).join(", ").toUpperCase()}` });
      setMail({ ...mail, brief: "" });
      await loadProposals();
      setOpenBroadcast(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération impossible"); } finally { setBusy(false); }
  };

  const setMode = async (real) => {
    try {
      const { data } = await api.post("/newsletter/config", { test_mode: !real });
      setSync((s) => ({ ...(s || {}), test_mode: data.test_mode }));
      setConfirmReal(false);
      toast.success(real ? "Mode réel activé — les envois à la liste partiront aux patients." : "Mode test réactivé — les envois liste sont bloqués.");
    } catch { toast.error("Changement de mode impossible"); }
  };

  const proposeFromUrl = async () => {
    if (!articleUrl.trim()) { toast.error("Collez d'abord le lien de l'article"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/proposals/from-url", { url: articleUrl.trim() });
      toast.success("Newsletter créée", { description: `${data.title || ""} · Langues : ${(data.langs || [data.lang]).join(", ").toUpperCase()}` });
      setArticleUrl("");
      loadProposals();
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible de créer la newsletter depuis ce lien"); }
    finally { setBusy(false); }
  };

  const loadProposals = () => api.get("/newsletter/proposals").then((r) => setProposals(r.data));
  const loadContacts = (status) => { const s = status ?? contactFilter; const q = s && s !== "all" ? `?status=${s}` : ""; return api.get(`/newsletter/contacts${q}`).then((r) => setContacts(r.data)); };
  const loadSuppression = () => api.get("/newsletter/suppression").then((r) => setSuppression(r.data));
  const loadSync = () => api.get("/newsletter/sync/status").then((r) => setSync(r.data)).catch(() => {});
  useEffect(() => { loadProposals(); loadContacts(); loadSuppression(); loadSync(); loadTemplates(); loadLangBreak(); loadHistory(); const t = setInterval(() => { loadProposals(); loadContacts(); loadHistory(); }, 12000); return () => clearInterval(t); }, [contactFilter]);

  const runSync = async (full = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/newsletter/sync${full ? "?full=true" : ""}`);
      if (data.ok) toast.success(full ? "Resynchronisation complète PhotoClinical" : "Synchronisation PhotoClinical", { description: `${data.added} ajoutés · ${data.updated} mis à jour · ${data.suppressed} bloqués (désinscrits)` });
      else toast.error(data.error || "Sync impossible");
      loadContacts(); loadSuppression(); loadSync(); loadLangBreak();
    } catch { toast.error("Sync impossible"); } finally { setBusy(false); }
  };

  const genProposals = async () => {
    setBusy(true);
    try {
      const p = source && source !== "auto" ? `?content_type=${source}` : "";
      const { data } = await api.post(`/newsletter/proposals/generate${p}`);
      if (data.created > 0) toast.success(`${data.created} proposition(s) préparée(s)`);
      else toast.info("Aucun nouvel article à proposer (déjà proposés récemment ou aucun disponible dans cette source).");
      loadProposals();
    } catch { toast.error("Génération impossible"); } finally { setBusy(false); }
  };

  const resubscribe = async (email) => {
    if (!window.confirm(`Réinscrire ${email} ?\n\nÀ ne faire que sur demande explicite de la personne. Elle recevra de nouveau la newsletter.`)) return;
    try {
      await api.post("/newsletter/resubscribe", { email });
      toast.success(`${email} réinscrit(e)`, { description: "Le contact est de nouveau actif et retiré de la liste de suppression." });
      loadContacts(); loadSuppression();
    } catch (e) { toast.error(e?.response?.data?.detail || "Réinscription impossible"); }
  };

  const addContact = async () => {
    if (!form.email) { toast.error("Email requis"); return; }
    try { await api.post("/newsletter/contacts", form); toast.success("Contact ajouté (actif)"); setForm({ email: "", first_name: "", last_name: "", lang: "es", consent_status: "active" }); loadContacts(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Ajout impossible"); }
  };

  const importFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("assume_consent", "true"); fd.append("lang_default", "es");
    setBusy(true);
    try { const { data } = await api.post("/newsletter/contacts/import", fd);
      toast.success(`Import: ${data.added} ajoutés (actifs) · ${data.updated} maj · ${data.suppressed} bloqués (désinscrits) · ${data.skipped} ignorés`);
      loadContacts(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Import impossible"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const setConsent = async (id, status) => { await api.patch(`/newsletter/contacts/${id}`, { consent_status: status }); loadContacts(); loadSuppression(); };
  const delContact = async (id) => { await api.delete(`/newsletter/contacts/${id}`); loadContacts(); };
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const bulkActivate = async () => {
    if (selected.length === 0) return;
    try { const { data } = await api.post("/newsletter/contacts/bulk-status", { ids: selected, status: "active" });
      toast.success(`${data.updated} contact(s) activé(s)`); setSelected([]); loadContacts(); }
    catch { toast.error("Activation impossible"); }
  };
  const pendingRows = contacts.contacts.filter((c) => c.consent_status === "pending");
  const articleProps = proposals.filter((p) => p.kind !== "broadcast");
  const broadcastProps = proposals.filter((p) => p.kind === "broadcast");

  const activateAllPending = async () => {
    const n = contacts.counts.pending || 0;
    if (!n) return;
    if (!window.confirm(`Activer les ${n} contact(s) « en attente » ?\n\nIls pourront alors recevoir la newsletter. Les désinscrits ne sont jamais touchés.`)) return;
    try {
      const { data } = await api.post("/newsletter/contacts/activate-all-pending");
      toast.success(`${data.activated} contact(s) activé(s)`);
      setSelected([]); loadContacts();
    } catch { toast.error("Activation impossible"); }
  };

  return (
    <div data-testid="newsletter-page">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-1">Communication patients</div>
        <h1 className="font-serif text-4xl text-brand tracking-tight">Newsletter</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Guardian ne rédige rien : il crée un e-mail qui reprend le titre et la description de l'article <b>tels qu'ils sont sur le site</b> et pointe vers l'article. Chaque patient reçoit le lien dans <b>sa langue</b>. Rien n'est envoyé sans votre validation.</p>
      </div>

      <div data-testid="test-mode-banner" className={`mb-5 flex items-center gap-3 border rounded-md px-4 py-3 text-sm ${sync?.test_mode === false ? "bg-emerald-50 border-emerald-300 text-emerald-900" : "bg-amber-50 border-amber-300 text-amber-900"}`}>
        {sync?.test_mode === false ? (
          <>
            <span className="font-mono text-[10px] uppercase tracking-wider bg-emerald-500 text-white px-2 py-0.5 rounded shrink-0">Mode réel</span>
            <span className="flex-1">Les envois <b>« à toute la liste »</b> partent réellement aux <b>patients actifs</b>. Le test perso reste requis avant chaque envoi.</span>
            <button data-testid="switch-test-mode-btn" onClick={() => setMode(false)}
              className="shrink-0 border border-emerald-400 text-emerald-800 hover:bg-emerald-100 px-3 py-1.5 rounded-md text-xs font-medium transition-colors">
              Revenir en mode test
            </button>
          </>
        ) : (
          <>
            <span className="font-mono text-[10px] uppercase tracking-wider bg-amber-300 text-amber-950 px-2 py-0.5 rounded shrink-0">Mode test</span>
            <span className="flex-1">Envoi en 2 temps : <b>1)</b> un <b>test</b> vers votre e-mail perso ({sync?.test_email || "votre adresse"}), puis <b>2)</b> l'envoi à <b>toute la liste</b>. L'envoi liste est <b>bloqué</b> tant que le mode test est actif.</span>
            <button data-testid="switch-real-mode-btn" onClick={() => setConfirmReal(true)}
              className="shrink-0 bg-brand hover:bg-brand-hover text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors">
              Passer en mode réel
            </button>
          </>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="proposals" data-testid="tab-proposals">Propositions ({articleProps.filter((p) => p.status === "proposed").length})</TabsTrigger>
          <TabsTrigger value="mailing" data-testid="tab-mailing">Mailing global ({broadcastProps.length})</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts ({contacts.counts.active || 0} actifs)</TabsTrigger>
          <TabsTrigger value="suppression" data-testid="tab-suppression">Liste de suppression ({contacts.counts.suppressed || 0})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Historique des envois ({history.total || 0})</TabsTrigger>
        </TabsList>

        {/* PROPOSALS */}
        <TabsContent value="proposals" className="mt-4">
          <div className="bg-white border border-border rounded-md p-3 mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground shrink-0">Créer depuis un lien du site</span>
              <input data-testid="article-url-input" value={articleUrl} onChange={(e) => setArticleUrl(e.target.value)}
                placeholder="https://clinicavalorian.es/es/…/mon-article"
                className="flex-1 min-w-[240px] px-3 py-2 rounded-md border border-border text-sm" />
              <button data-testid="from-url-btn" onClick={proposeFromUrl} disabled={busy}
                className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60">
                <PaperPlaneTilt size={15} /> Créer la newsletter
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Collez n'importe quel lien d'article du site. Guardian détecte automatiquement les versions <b>ES / FR / EN / RU</b> et enverra à chaque patient le lien dans sa langue (repli espagnol).</p>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
              <button data-testid="refresh-contacts-btn" onClick={() => runSync(false)} disabled={busy || !sync?.configured}
                className="flex items-center gap-2 border border-brand text-brand hover:bg-brand-soft px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-60">
                {busy ? <CircleNotch size={14} className="ai-pulse" /> : <ArrowsClockwise size={14} />} Rafraîchir les contacts
              </button>
              <span className="text-xs text-muted-foreground">
                {sync?.configured
                  ? <>Récupère les patients ajoutés récemment dans PhotoClinical · <b>{contacts.counts.active || 0}</b> actifs, <b>{contacts.counts.pending || 0}</b> en attente</>
                  : "Synchronisation PhotoClinical non configurée"}
              </span>
            </div>
          </div>
          <div className="flex justify-end items-center gap-2 mb-3">
            <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Source</span>
            <select data-testid="proposal-source-select" value={source} onChange={(e) => setSource(e.target.value)}
              className="px-3 py-2 rounded-md border border-border text-sm bg-white">
              <option value="auto">Auto (recommandé)</option>
              <option value="medico_responde">El Doctor Florian Vallecillo te explica</option>
              <option value="scientific">Publications scientifiques</option>
              <option value="patient_guide">Guías para Pacientes</option>
              <option value="treatment_sheet">Fichas de Tratamiento</option>
              <option value="faq">FAQ</option>
            </select>
            <button data-testid="gen-proposals-btn" onClick={genProposals} disabled={busy}
              className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60">
              {busy ? <CircleNotch size={16} className="ai-pulse" /> : <Sparkle size={16} weight="fill" />} Générer des propositions
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {articleProps.length === 0 && <div className="text-sm text-muted-foreground col-span-2 bg-white border border-border rounded-md p-6 text-center">Aucune proposition. Cliquez « Générer des propositions ».</div>}
            {articleProps.map((p) => (
              <div key={p.id} data-testid={`proposal-${p.id}`} onClick={() => setOpen(p.id)}
                className="bg-white border border-border rounded-md p-4 cursor-pointer hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <Pill map={PROP_STATUS} k={p.status} />
                  {(p.langs && p.langs.length > 0 ? p.langs : [p.lang]).map((l) => (
                    <span key={l} className="text-[10px] font-mono uppercase text-muted-foreground border border-border rounded px-1.5 py-0.5" data-testid={`proposal-lang-${p.id}-${l}`}>{l}</span>
                  ))}
                  {p.collection && <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-soft text-brand font-medium" data-testid={`proposal-origin-${p.id}`}>{p.collection}</span>}
                  {p.status === "approved" && p.scheduled_at && (
                    <span className="ml-auto flex items-center gap-1 text-[11px] text-blue-700" data-testid={`proposal-sched-${p.id}`}>
                      ⏰ {new Date(p.scheduled_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      <button data-testid={`proposal-cancel-${p.id}`} onClick={(e) => cancelSchedule(p.id, e)} className="text-red-600 hover:underline ml-1">Annuler</button>
                    </span>
                  )}
                  {p.status === "sent" && <span className="ml-auto text-[11px] font-mono text-muted-foreground">{p.recipients || 0} envois</span>}
                </div>
                <div className="text-sm font-medium leading-snug">{p.subject}</div>
                <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.intro}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* MAILING GLOBAL */}
        <TabsContent value="mailing" className="mt-4">
          <div className="bg-white border border-border rounded-md p-4 mb-3">
            <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Nouveau mail d'information global</div>
            <textarea data-testid="mail-brief" value={mail.brief} onChange={(e) => setMail({ ...mail, brief: e.target.value })}
              placeholder="Décrivez le message à envoyer à tous les patients (ex. « Annonce : notre clinique évolue et rejoint la Clínica Valorian. Nous continuons votre suivi avec la même attention dans nos nouveaux locaux. »)"
              className="w-full min-h-[84px] px-3 py-2 rounded-md border border-border text-sm" />
            <div className="flex items-center gap-4 flex-wrap mt-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Ton</span>
                <select data-testid="mail-tone" value={mail.tone} onChange={(e) => setMail({ ...mail, tone: e.target.value })} className="px-3 py-2 rounded-md border border-border text-sm bg-white">
                  <option value="chaleureux">Chaleureux</option>
                  <option value="preventif">Préventif</option>
                  <option value="informatif">Informatif</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Langues</span>
                {ALL_LANGS.map(([code]) => (
                  <label key={code} className="flex items-center gap-1 text-xs cursor-pointer">
                    <input type="checkbox" data-testid={`mail-lang-${code}`} checked={mail.languages.includes(code)} onChange={() => toggleLang(code)} /> {code.toUpperCase()}
                  </label>
                ))}
              </div>
              <button data-testid="gen-campaign-btn" onClick={generateCampaign} disabled={busy}
                className="ml-auto flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60">
                {busy ? <CircleNotch size={16} className="ai-pulse" /> : <Sparkle size={16} weight="fill" />} Générer avec l'IA
              </button>
            </div>
            <input data-testid="mail-signature" value={mail.signature} onChange={(e) => setMail({ ...mail, signature: e.target.value })}
              className="w-full mt-3 px-3 py-2 rounded-md border border-border text-sm" placeholder="Signature" />
            <p className="text-xs text-muted-foreground mt-2">L'IA rédige objet + message dans les langues choisies. Vous pourrez relire, modifier, envoyer un test, puis envoyer à <b>tous les patients actifs</b> (chacun dans sa langue). Lien de désinscription ajouté automatiquement.</p>
          </div>
          {templates.length > 0 && (
            <div className="bg-white border border-border rounded-md p-3 mb-3">
              <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Modèles réutilisables</div>
              <div className="flex flex-wrap gap-2">
                {templates.map((t) => (
                  <div key={t.id} data-testid={`template-${t.id}`} className="flex items-center gap-2 border border-border rounded-full pl-3 pr-1 py-1 bg-secondary/40">
                    <button onClick={() => applyTemplate(t.id)} className="text-xs font-medium hover:text-brand" data-testid={`template-use-${t.id}`}>{t.name}</button>
                    <span className="text-[10px] text-muted-foreground">{(t.langs || []).join("·").toUpperCase()}</span>
                    <button onClick={(e) => delTemplate(t.id, e)} data-testid={`template-del-${t.id}`} className="text-muted-foreground hover:text-red-600 rounded-full w-5 h-5 flex items-center justify-center"><X size={12} /></button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Cliquez un modèle pour créer un nouveau mailing prêt à relire, tester et envoyer.</p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {broadcastProps.length === 0 && <div className="text-sm text-muted-foreground col-span-2 bg-white border border-border rounded-md p-6 text-center">Aucun mailing. Rédigez un brief et cliquez « Générer avec l'IA ».</div>}
            {broadcastProps.map((p) => (
              <div key={p.id} data-testid={`broadcast-${p.id}`} onClick={() => setOpenBroadcast(p.id)}
                className="bg-white border border-border rounded-md p-4 cursor-pointer hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <Pill map={PROP_STATUS} k={p.status} />
                  {(p.langs && p.langs.length ? p.langs : [p.lang]).map((l) => (
                    <span key={l} className="text-[10px] font-mono uppercase text-muted-foreground border border-border rounded px-1.5 py-0.5">{l}</span>
                  ))}
                  {p.status === "approved" && p.scheduled_at && (
                    <span className="ml-auto flex items-center gap-1 text-[11px] text-blue-700" data-testid={`broadcast-sched-${p.id}`}>
                      ⏰ {new Date(p.scheduled_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      <button data-testid={`broadcast-cancel-${p.id}`} onClick={(e) => cancelSchedule(p.id, e)} className="text-red-600 hover:underline ml-1">Annuler</button>
                    </span>
                  )}
                  {p.status === "sent" && <span className="ml-auto text-[11px] font-mono text-muted-foreground">{p.recipients || 0} envois</span>}
                </div>
                <div className="text-sm font-medium leading-snug">{p.subject}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* CONTACTS */}
        <TabsContent value="contacts" className="mt-4">          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap bg-white border border-border rounded-md px-4 py-3">
            <div className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Synchronisation PhotoClinical</span>
              {sync?.configured
                ? <> · Dernière synchro : <b>{sync.last_synced_at ? new Date(sync.last_synced_at).toLocaleString("fr-FR") : "jamais"}</b>
                    {sync.last_result && <> · {sync.last_result.added} ajoutés, {sync.last_result.updated} maj</>}
                    {sync.last_error && <span className="text-red-600"> · Erreur : {sync.last_error}</span>}</>
                : <span className="text-amber-600"> · non configurée</span>}
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="nl-sync-btn" onClick={() => runSync(false)} disabled={busy || !sync?.configured}
                className="flex items-center gap-2 border border-brand text-brand hover:bg-brand-soft px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60">
                {busy ? <CircleNotch size={15} className="ai-pulse" /> : <ArrowsClockwise size={15} />} Synchroniser maintenant
              </button>
              <button data-testid="nl-sync-full-btn" onClick={() => runSync(true)} disabled={busy || !sync?.configured}
                title="Récupère TOUS les patients de PhotoClinical (pas seulement les nouveautés)"
                className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60">
                {busy ? <CircleNotch size={15} className="ai-pulse" /> : <ArrowsClockwise size={15} weight="bold" />} Resynchroniser tout
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex flex-wrap gap-2 items-center">
              <button data-testid="filter-all" onClick={() => setContactFilter("all")}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${contactFilter === "all" ? "bg-brand text-white border-brand" : "bg-white border-border hover:bg-secondary"}`}>
                Tous
              </button>
              {Object.entries(CONSENT).map(([k, [l, c]]) => (
                <button key={k} data-testid={`filter-${k}`} onClick={() => setContactFilter(k)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${contactFilter === k ? "text-white" : "bg-white hover:bg-secondary"}`}
                  style={contactFilter === k ? { background: c, borderColor: c } : { color: c, borderColor: "var(--border)" }}>
                  {l}: <b>{contacts.counts[k] || 0}</b>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {(contacts.counts.pending || 0) > 0 && (
                <button data-testid="activate-all-pending-btn" onClick={activateAllPending}
                  className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
                  <Check size={15} weight="bold" /> Tout valider ({contacts.counts.pending} en attente)
                </button>
              )}
              {selected.length > 0 && (
                <button data-testid="bulk-activate-btn" onClick={bulkActivate}
                  className="flex items-center gap-2 border border-brand text-brand hover:bg-brand-soft px-4 py-2 rounded-md text-sm font-medium transition-colors">
                  <Check size={15} weight="bold" /> Activer la sélection ({selected.length})
                </button>
              )}
            </div>
          </div>

          <div className="bg-white border border-border rounded-md p-4 mb-4">
            <DataLabel>Ajouter un contact</DataLabel>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mt-3 items-end">
              <input data-testid="nl-email" placeholder="email@…" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="md:col-span-2 px-3 py-2 rounded-md border border-border text-sm" />
              <input placeholder="Prénom" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="px-3 py-2 rounded-md border border-border text-sm" />
              <input placeholder="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="px-3 py-2 rounded-md border border-border text-sm" />
              <select value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })} className="px-3 py-2 rounded-md border border-border text-sm bg-white">
                {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <select value={form.consent_status} onChange={(e) => setForm({ ...form, consent_status: e.target.value })} className="px-3 py-2 rounded-md border border-border text-sm bg-white">
                <option value="active">Actif (par défaut)</option>
                <option value="pending">En attente (à activer plus tard)</option>
              </select>
            </div>
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-3">
                <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={importFile} className="hidden" data-testid="nl-import-file" />
                <button data-testid="nl-import-btn" onClick={() => fileRef.current?.click()} disabled={busy}
                  className="flex items-center gap-1.5 text-xs border border-border hover:bg-secondary px-3 py-2 rounded-md transition-colors disabled:opacity-60">
                  <UploadSimple size={14} /> Importer un fichier (CSV / XLSX)
                </button>
                <span className="text-xs text-muted-foreground">Colonne <b>email</b> requise (prénom, nom, langue optionnels). Import en <b>Actif</b>. Les désinscrits ne sont jamais réactivés.</span>
              </div>
              <button data-testid="nl-add-contact-btn" onClick={addContact} className="bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">Ajouter</button>
            </div>
          </div>

          <div className="bg-white border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 w-8">
                    <input type="checkbox" data-testid="select-all-pending" title="Sélectionner tous les « En attente »"
                      checked={pendingRows.length > 0 && selected.length === pendingRows.length}
                      onChange={(e) => setSelected(e.target.checked ? pendingRows.map((c) => c.id) : [])} />
                  </th>
                  <th className="px-4 py-3">Email</th><th className="px-4 py-3">Nom</th><th className="px-4 py-3">Langue</th><th className="px-4 py-3">Consentement</th><th className="px-4 py-3">Source</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {contacts.contacts.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Aucun contact.</td></tr>}
                {contacts.contacts.map((c) => (
                  <tr key={c.id} data-testid={`contact-row-${c.id}`} className="hover:bg-secondary/40">
                    <td className="px-4 py-2.5">
                      {c.consent_status === "pending" && (
                        <input type="checkbox" data-testid={`select-contact-${c.id}`}
                          checked={selected.includes(c.id)} onChange={() => toggleSel(c.id)} />
                      )}
                    </td>
                    <td className="px-4 py-2.5">{c.email}</td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs">{[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}</td>
                    <td className="px-4 py-2.5 text-xs uppercase">{c.lang}</td>
                    <td className="px-4 py-2.5">
                      <select value={c.consent_status} onChange={(e) => setConsent(c.id, e.target.value)} className="text-xs border border-border rounded px-2 py-1 bg-white">
                        {Object.entries(CONSENT).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{c.source}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {c.consent_status === "unsubscribed" && (
                          <button data-testid={`resub-contact-${c.id}`} onClick={() => resubscribe(c.email)}
                            className="text-xs border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded-md transition-colors">
                            Réinscrire
                          </button>
                        )}
                        <button data-testid={`del-contact-${c.id}`} onClick={() => delContact(c.id)} className="text-muted-foreground hover:text-red-600"><Trash size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* SUPPRESSION */}
        <TabsContent value="suppression" className="mt-4">
          <p className="text-sm text-muted-foreground mb-3">Adresses désinscrites ou en erreur. Elles ne reçoivent plus d'e-mail et ne sont <b>jamais</b> réactivées par un import ou une synchronisation. Vous pouvez toutefois <b>réinscrire manuellement</b> une personne qui vous en fait la demande.</p>
          <div className="bg-white border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                <tr><th className="px-4 py-3">Email</th><th className="px-4 py-3">Raison</th><th className="px-4 py-3">Date</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {suppression.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Liste vide.</td></tr>}
                {suppression.map((s) => (
                  <tr key={s.id}>
                    <td className="px-4 py-2.5">{s.email}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{s.reason}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button data-testid={`resub-suppression-${s.id}`} onClick={() => resubscribe(s.email)}
                        className="text-xs border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded-md transition-colors">
                        Réinscrire
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* HISTORIQUE DES ENVOIS */}
        <TabsContent value="history" className="mt-4">
          <p className="text-sm text-muted-foreground mb-3">Journal chronologique des campagnes <b>réellement envoyées</b> (newsletters d'articles et mailings globaux), de la plus récente à la plus ancienne. Utile pour voir quand remonte le dernier envoi et éviter d'envoyer trop souvent.</p>
          {history.last_sent_at ? (
            <div data-testid="history-last-sent" className="mb-4 flex items-center gap-3 bg-brand-soft/50 border border-brand/30 rounded-md px-4 py-3 text-sm">
              <span className="font-mono text-[10px] uppercase tracking-wider bg-brand text-white px-2 py-0.5 rounded shrink-0">Dernier envoi</span>
              <span className="flex-1"><b>{new Date(history.last_sent_at).toLocaleString("fr-FR", { dateStyle: "full", timeStyle: "short" })}</b> — {relDays(history.last_sent_at)}</span>
            </div>
          ) : (
            <div className="mb-4 text-sm text-muted-foreground">Aucune campagne envoyée pour l'instant.</div>
          )}
          <div className="bg-white border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Date &amp; heure</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Objet</th>
                  <th className="px-4 py-3 text-right">Envoyés</th>
                  <th className="px-4 py-3 text-right">Ouvertures</th>
                  <th className="px-4 py-3 text-right">Clics</th>
                  <th className="px-4 py-3 text-right">Désinsc.</th>
                  <th className="px-4 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {history.campaigns.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Aucun envoi enregistré.</td></tr>}
                {history.campaigns.map((c) => (
                  <tr key={c.id} data-testid={`history-row-${c.id}`} className="hover:bg-secondary/40">
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <div className="font-medium">{new Date(c.sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</div>
                      <div className="text-[11px] text-muted-foreground">{new Date(c.sent_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} · {relDays(c.sent_at)}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full ${c.kind === "broadcast" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>{c.type_label}</span>
                    </td>
                    <td className="px-4 py-2.5 max-w-[300px] truncate" title={c.subject}>{c.subject}</td>
                    <td className="px-4 py-2.5 text-right font-medium">{c.sent.toLocaleString("fr-FR")}</td>
                    <td className="px-4 py-2.5 text-right">{c.opened} <span className="text-[11px] text-muted-foreground">({c.open_rate}%)</span></td>
                    <td className="px-4 py-2.5 text-right">{c.clicked} <span className="text-[11px] text-muted-foreground">({c.click_rate}%)</span></td>
                    <td className="px-4 py-2.5 text-right">{c.unsubscribed}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button data-testid={`history-delivery-${c.id}`} onClick={() => setDeliveryId(c.id)}
                        className="text-xs border border-border hover:bg-secondary px-3 py-1 rounded-md whitespace-nowrap">État / Reprise</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <ProposalDialog id={open} langBreak={langBreak} onClose={() => { setOpen(null); loadProposals(); }} />
      <BroadcastDialog id={openBroadcast} langBreak={langBreak} onSavedTemplate={loadTemplates} onClose={() => { setOpenBroadcast(null); loadProposals(); }} />
      <DeliveryDialog id={deliveryId} onResumed={loadHistory} onClose={() => setDeliveryId(null)} />
      <BroadcastDialog id={openBroadcast} langBreak={langBreak} onSavedTemplate={loadTemplates} onClose={() => { setOpenBroadcast(null); loadProposals(); }} />

      <Dialog open={confirmReal} onOpenChange={(o) => !o && setConfirmReal(false)}>
        <DialogContent className="max-w-md" data-testid="confirm-real-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-brand">Passer en mode réel ?</DialogTitle>
            <DialogDescription>
              À partir de maintenant, le bouton « Envoyer à toute la liste » enverra la newsletter aux <b>{contacts.counts.active || 0} contact(s) actif(s)</b>. Un test perso reste obligatoire avant chaque envoi. Les désinscriptions et la liste de suppression restent respectées.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end mt-2">
            <button onClick={() => setConfirmReal(false)} className="border border-border hover:bg-secondary px-4 py-2 rounded-md text-sm">Annuler</button>
            <button data-testid="confirm-real-btn" onClick={() => setMode(true)} className="bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium">Activer le mode réel</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DeliveryDialog({ id, onResumed, onClose }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!id) { setD(null); return; }
    setD(null);
    api.get(`/newsletter/proposals/${id}/delivery`).then((r) => setD(r.data)).catch(() => {});
  }, [id]);
  const resume = async () => {
    if (!d) return;
    if (!window.confirm(`Reprendre l'envoi UNIQUEMENT pour les ${d.remaining} destinataire(s) restant(s) ?\n\nLes ${d.sent_ok} déjà envoyés avec succès ne seront PAS renvoyés. L'historique est conservé.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/newsletter/proposals/${id}/resume`);
      if (data.resuming) toast.success("Reprise lancée", { description: `${data.remaining} envoi(s) restant(s) en cours. ${data.already_sent} déjà envoyés préservés.` });
      else toast.info(data.message || "Rien à reprendre.");
      onResumed && onResumed();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reprise impossible"); }
    finally { setBusy(false); }
  };
  if (!id) return null;
  const Stat = ({ label, value, color }) => (
    <div className="border border-border rounded-md px-4 py-3 bg-white">
      <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-serif mt-0.5" style={{ color }}>{value != null ? value.toLocaleString("fr-FR") : "…"}</div>
    </div>
  );
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="delivery-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-brand">État de livraison</DialogTitle>
          <DialogDescription>{d?.subject || "Chargement…"}</DialogDescription>
        </DialogHeader>
        {!d ? <div className="p-8 text-center text-muted-foreground text-sm">Chargement…</div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Déjà envoyés" value={d.sent_ok} color="#166534" />
              <Stat label="Restant à envoyer" value={d.remaining} color="#b45309" />
              <Stat label="Échecs (à renvoyer)" value={d.failed} color="#dc2626" />
              <Stat label="Bloqués (désinscrits)" value={d.blocked} color="#94a3b8" />
            </div>
            <div className="text-xs text-muted-foreground">
              Contacts actifs : <b>{(d.active_total || 0).toLocaleString("fr-FR")}</b>. La reprise enverra le message
              uniquement aux <b>{d.remaining}</b> destinataire(s) restant(s) — les <b>{d.sent_ok}</b> déjà reçus sont conservés (aucun doublon).
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button data-testid="delivery-close" onClick={onClose} className="border border-border hover:bg-secondary px-4 py-2 rounded-md text-sm">Fermer</button>
              <button data-testid="delivery-resume" onClick={resume} disabled={busy || !d.remaining}
                title={!d.remaining ? "Aucun destinataire restant" : ""}
                className="bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50">
                {busy ? "Reprise…" : `Reprendre les envois restants (${d.remaining})`}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BroadcastDialog({ id, langBreak, onSavedTemplate, onClose }) {
  const [p, setP] = useState(null);
  const [lang, setLang] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState({ subject: "", body_html: "" });
  const [stats, setStats] = useState(null);
  const [schedule, setSchedule] = useState("");
  const [askWhen, setAskWhen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!id) { setP(null); setStats(null); return; }
    api.get(`/newsletter/proposals/${id}`).then(({ data }) => {
      setP(data);
      const l = (data.langs && data.langs.length ? data.langs : [data.lang])[0];
      setLang(l);
      const e = (data.i18n || {})[l] || {};
      setEdit({ subject: e.subject || "", body_html: e.body_html || "" });
    });
    api.get(`/newsletter/proposals/${id}/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, [id]);

  const switchLang = (l) => { setLang(l); const e = (p.i18n || {})[l] || {}; setEdit({ subject: e.subject || "", body_html: e.body_html || "" }); };
  const save = async () => { setBusy(true); try { const { data } = await api.patch(`/newsletter/proposals/${id}/i18n`, { lang, subject: edit.subject, body_html: edit.body_html }); setP(data); toast.success("Enregistré"); } catch { toast.error("Échec"); } finally { setBusy(false); } };
  const sendTest = async () => { setBusy(true); try { await save(); await api.post(`/newsletter/proposals/${id}/send-test`); const { data } = await api.get(`/newsletter/proposals/${id}`); setP(data); toast.success("E-mail de test envoyé", { description: "Vérifiez votre boîte perso, puis envoyez à tous les patients." }); } catch (e) { toast.error(e?.response?.data?.detail || "Test impossible"); } finally { setBusy(false); } };
  const doSend = async (scheduledAtIso) => { setBusy(true); try { await save(); const body = scheduledAtIso ? { scheduled_at: scheduledAtIso } : {}; const data = await postSend(id, body); if (!data) return; if (data.scheduled) toast.success(`Envoi programmé le ${new Date(data.at).toLocaleString("fr-FR")}`, { description: `${data.recipients} patient(s) actif(s)` }); else toast.success(`Envoi en cours vers ${data.recipients} patient(s)`); onClose(); } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); } finally { setBusy(false); } };
  const reject = async () => { setBusy(true); try { await api.post(`/newsletter/proposals/${id}/reject`); toast.success("Mailing supprimé"); onClose(); } catch { toast.error("Échec"); } finally { setBusy(false); } };
  const saveAsTemplate = async () => { const name = window.prompt("Nom du modèle ?", (p && p.subject) || "Mailing"); if (!name) return; try { await api.post("/newsletter/templates", { proposal_id: id, name }); toast.success("Modèle enregistré", { description: "Réutilisable en un clic depuis l'onglet Mailing global." }); onSavedTemplate && onSavedTemplate(); } catch { toast.error("Échec de l'enregistrement du modèle"); } };
  const insertImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image trop lourde (max 5 Mo)."); if (fileRef.current) fileRef.current.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/newsletter/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const tag = `<img src="${data.url}" alt="" style="max-width:100%;height:auto;border-radius:8px;display:block;margin:12px 0;" />`;
      setEdit((prev) => ({ ...prev, body_html: (prev.body_html ? prev.body_html + "\n" : "") + tag }));
      toast.success("Image insérée", { description: "Elle apparaît dans l'aperçu ci-dessous. Pensez à Enregistrer." });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'upload de l'image");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!id) return null;
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="broadcast-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-brand">Mail d'information global</DialogTitle>
          <DialogDescription>Relisez et modifiez le message par langue, envoyez un test, puis envoyez à tous les patients actifs. Aucun envoi sans validation.</DialogDescription></DialogHeader>
        {!p ? <div className="p-8 text-center text-muted-foreground text-sm">Chargement…</div> : (
          <div className="space-y-3">
            {p.status === "sent" && stats && (
              <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md px-3 py-2">
                Envoyé à {stats.sent} · Ouverts {stats.opened} ({stats.open_rate}%) · Clics {stats.clicked}
              </div>
            )}
            <div className="flex gap-1 flex-wrap">
              {(p.langs && p.langs.length ? p.langs : [p.lang]).map((l) => (
                <button key={l} data-testid={`bc-lang-${l}`} onClick={() => switchLang(l)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${lang === l ? "bg-brand text-white border-brand" : "bg-white border-border hover:bg-secondary"}`}>{l.toUpperCase()}</button>
              ))}
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Objet</label>
              <input data-testid="bc-subject" value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-md border border-border text-sm font-medium" placeholder="Objet de l'e-mail" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Message (HTML simple)</label>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif" onChange={insertImage} className="hidden" data-testid="bc-image-input" />
                <button type="button" data-testid="bc-insert-image" onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading}
                  className="inline-flex items-center gap-1.5 text-xs border border-border hover:bg-secondary px-3 py-1.5 rounded-md disabled:opacity-50">
                  {uploading ? <CircleNotch className="w-3.5 h-3.5 animate-spin" /> : <UploadSimple className="w-3.5 h-3.5" />}
                  {uploading ? "Envoi…" : "Insérer une image"}
                </button>
              </div>
              <textarea data-testid="bc-body" value={edit.body_html} onChange={(e) => setEdit({ ...edit, body_html: e.target.value })}
                className="w-full mt-1 min-h-[170px] px-3 py-2 rounded-md border border-border text-xs font-mono" placeholder="<p>…</p>" />
              <div className="text-[11px] text-muted-foreground mt-1">Image : PNG / JPG / WEBP · max 5 Mo — insérée en pleine largeur dans l'e-mail.</div>
            </div>
            <div className="border border-border rounded-md p-3 bg-secondary/40">
              <div className="text-[11px] font-mono uppercase text-muted-foreground mb-2">Aperçu</div>
              <div className="bg-white rounded p-3 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: edit.body_html }} />
              {p.signature && <div className="text-sm text-brand mt-2 px-3">{p.signature}</div>}
            </div>
            <div className="flex items-center gap-2 justify-end pt-1 flex-wrap">
              <button data-testid="bc-reject" onClick={reject} disabled={busy} className="text-muted-foreground hover:text-red-600 px-3 py-2 rounded-md text-sm mr-auto">Supprimer</button>
              <button data-testid="bc-save-template" onClick={saveAsTemplate} disabled={busy} className="border border-border hover:bg-secondary px-4 py-2 rounded-md text-sm">Enregistrer comme modèle</button>
              <button data-testid="bc-save" onClick={save} disabled={busy} className="border border-border hover:bg-secondary px-4 py-2 rounded-md text-sm">Enregistrer</button>
              <button data-testid="bc-send-test" onClick={sendTest} disabled={busy} className="border border-brand text-brand hover:bg-brand-soft px-4 py-2 rounded-md text-sm font-medium">Envoyer un test</button>
              <button data-testid="bc-send" onClick={() => setAskWhen(true)} disabled={busy || !p.test_sent_at} title={!p.test_sent_at ? "Envoyez d'abord un test" : ""}
                className="bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50">Envoyer à tous</button>
            </div>
            {langBreak && <div className="text-xs text-muted-foreground text-right" data-testid="bc-langbreak">Destinataires actifs : <b>{langBreak.total}</b> (ES {langBreak.es} · FR {langBreak.fr} · EN {langBreak.en} · RU {langBreak.ru})</div>}
            {askWhen && (
              <div className="border border-brand/40 bg-brand-soft/40 rounded-md p-3 mt-1" data-testid="bc-when-panel">
                <div className="text-sm font-medium mb-2">Envoyer quand ?</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button data-testid="bc-send-now" onClick={() => doSend(null)} disabled={busy} className="bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium">Maintenant</button>
                  <span className="text-muted-foreground text-sm">ou</span>
                  <input type="datetime-local" data-testid="bc-schedule" value={schedule} min={new Date().toISOString().slice(0, 16)} onChange={(e) => setSchedule(e.target.value)} className="px-2 py-1 rounded-md border border-border text-sm" />
                  <button data-testid="bc-send-schedule" onClick={() => doSend(new Date(schedule).toISOString())} disabled={busy || !schedule} className="border border-brand text-brand hover:bg-brand-soft px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40">Programmer</button>
                  <button data-testid="bc-when-cancel" onClick={() => setAskWhen(false)} className="text-muted-foreground hover:underline text-sm ml-auto">Annuler</button>
                </div>
              </div>
            )}
            {!p.test_sent_at && <p className="text-xs text-amber-600 text-right">Envoyez d'abord un test pour débloquer l'envoi à tous.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function ProposalDialog({ id, langBreak, onClose }) {
  const [p, setP] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState({});
  const [schedule, setSchedule] = useState("");
  const [askWhen, setAskWhen] = useState(false);

  useEffect(() => {
    if (!id) { setP(null); setStats(null); return; }
    api.get(`/newsletter/proposals/${id}`).then((r) => { setP(r.data); setEdit({ subject: r.data.subject, title: r.data.title, intro: r.data.intro, cta_label: r.data.cta_label }); });
    api.get(`/newsletter/proposals/${id}/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, [id]);

  const save = async () => { setBusy(true); try { await api.patch(`/newsletter/proposals/${id}`, edit); const { data } = await api.get(`/newsletter/proposals/${id}`); setP(data); toast.success("Modifications enregistrées"); } catch { toast.error("Échec"); } finally { setBusy(false); } };
  const sendTest = async () => { setBusy(true); try { await save(); await api.post(`/newsletter/proposals/${id}/send-test`); const { data } = await api.get(`/newsletter/proposals/${id}`); setP(data); toast.success("E-mail de test envoyé", { description: "Vérifiez votre boîte perso, puis envoyez à toute la liste." }); } catch (e) { toast.error(e?.response?.data?.detail || "Test impossible"); } finally { setBusy(false); } };
  const doSend = async (scheduledAtIso) => { setBusy(true); try { await save(); const body = scheduledAtIso ? { scheduled_at: scheduledAtIso } : {}; const data = await postSend(id, body); if (!data) return; if (data.scheduled) toast.success(`Envoi programmé le ${new Date(data.at).toLocaleString("fr-FR")}`, { description: `${data.recipients} patient(s) actif(s)` }); else toast.success(`Envoi en cours vers ${data.recipients} patient(s)`); onClose(); } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); } finally { setBusy(false); } };
  const reject = async () => { setBusy(true); try { await api.post(`/newsletter/proposals/${id}/reject`); toast.success("Proposition refusée"); onClose(); } catch { toast.error("Échec"); } finally { setBusy(false); } };

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-brand">Proposition de newsletter</DialogTitle>
          <DialogDescription>Aperçu de l'e-mail. Modifiez si besoin, puis validez l'envoi (aucun envoi sans validation).</DialogDescription></DialogHeader>
        {!p ? <div className="p-8 text-center text-muted-foreground text-sm">Chargement…</div> : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="space-y-3">
              <div><label className="text-[11px] font-mono uppercase text-muted-foreground">Objet de l'e-mail</label>
                <input data-testid="prop-subject" value={edit.subject || ""} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border text-sm" /></div>
              <div><label className="text-[11px] font-mono uppercase text-muted-foreground">Titre</label>
                <input value={edit.title || ""} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border text-sm font-serif text-brand" /></div>
              <div><label className="text-[11px] font-mono uppercase text-muted-foreground">Introduction</label>
                <textarea data-testid="prop-intro" value={edit.intro || ""} rows={4} onChange={(e) => setEdit({ ...edit, intro: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border text-sm" /></div>
              <div><label className="text-[11px] font-mono uppercase text-muted-foreground">Bouton</label>
                <input value={edit.cta_label || ""} onChange={(e) => setEdit({ ...edit, cta_label: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border text-sm" /></div>
              <div className="text-[11px] text-muted-foreground">Langue : <b className="uppercase">{p.lang}</b>{p.collection && <> · Origine : <b>{p.collection}</b></>} · Lien article (UTM) : <a href={p.article_url} target="_blank" rel="noreferrer" className="text-ai break-all">ouvrir ↗</a></div>

              {p.status === "sent" && stats && (
                <div className="mt-2 border-t border-border pt-3" data-testid="prop-stats">
                  <DataLabel>Statistiques</DataLabel>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-center">
                    {[["Envoyés", stats.sent], ["Délivrés", stats.delivered], ["Ouvertures", `${stats.opened} (${stats.open_rate}%)`], ["Clics", `${stats.clicked} (${stats.click_rate}%)`], ["Bounces", stats.bounced], ["Désinsc.", stats.unsubscribed]].map(([l, v]) => (
                      <div key={l} className="bg-secondary rounded p-2"><div className="text-[10px] text-muted-foreground uppercase">{l}</div><div className="font-mono text-sm">{v}</div></div>
                    ))}
                  </div>
                </div>
              )}

              {p.status !== "sent" && p.status !== "rejected" && (
                <div className="pt-3 border-t border-border space-y-2">
                  {p.test_sent_at && <div className="text-[11px] text-emerald-700" data-testid="test-sent-note">✓ Test envoyé le {new Date(p.test_sent_at).toLocaleString("fr-FR")} — vérifiez votre boîte perso avant l'envoi à la liste.</div>}
                  <div className="flex gap-2">
                    <button data-testid="prop-sendtest-btn" onClick={sendTest} disabled={busy}
                      className="flex-1 flex items-center justify-center gap-2 border border-brand text-brand hover:bg-brand-soft py-2.5 rounded-md text-sm font-medium disabled:opacity-60">
                      <PaperPlaneTilt size={15} /> 1. Envoyer un test (perso)
                    </button>
                    <button data-testid="prop-send-btn" onClick={() => setAskWhen(true)} disabled={busy || !p.test_sent_at}
                      title={!p.test_sent_at ? "Envoyez d'abord un test" : ""}
                      className="flex-1 flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover text-white py-2.5 rounded-md text-sm font-medium disabled:opacity-40">
                      <Check size={15} weight="bold" /> 2. Envoyer à toute la liste
                    </button>
                  </div>
                  {askWhen && (
                    <div className="border border-brand/40 bg-brand-soft/40 rounded-md p-3" data-testid="prop-when-panel">
                      <div className="text-sm font-medium mb-2">Envoyer quand ?</div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button data-testid="prop-send-now" onClick={() => doSend(null)} disabled={busy} className="bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md text-sm font-medium">Maintenant</button>
                        <span className="text-muted-foreground text-sm">ou</span>
                        <input type="datetime-local" data-testid="prop-schedule" value={schedule} min={new Date().toISOString().slice(0, 16)} onChange={(e) => setSchedule(e.target.value)} className="px-2 py-1 rounded-md border border-border text-sm" />
                        <button data-testid="prop-send-schedule" onClick={() => doSend(new Date(schedule).toISOString())} disabled={busy || !schedule} className="border border-brand text-brand hover:bg-brand-soft px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40">Programmer</button>
                        <button data-testid="prop-when-cancel" onClick={() => setAskWhen(false)} className="text-muted-foreground hover:underline text-sm ml-auto">Annuler</button>
                      </div>
                    </div>
                  )}
                  {langBreak && <div className="text-xs text-muted-foreground" data-testid="prop-langbreak">Destinataires actifs : <b>{langBreak.total}</b> (ES {langBreak.es} · FR {langBreak.fr} · EN {langBreak.en} · RU {langBreak.ru})</div>}
                  <div className="flex gap-2">
                    <button data-testid="prop-save-btn" onClick={save} disabled={busy} className="flex-1 border border-border hover:bg-secondary py-2 rounded-md text-sm">Enregistrer</button>
                    <button data-testid="prop-reject-btn" onClick={reject} disabled={busy} className="border border-red-200 text-red-600 hover:bg-red-50 px-4 py-2 rounded-md text-sm"><X size={15} /></button>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase text-muted-foreground flex items-center gap-1.5"><Eye size={13} /> Aperçu de l'e-mail</label>
              <iframe data-testid="prop-preview" title="preview" srcDoc={p.preview_html} className="mt-2 w-full h-[560px] border border-border rounded-md bg-white" />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
